import { ActionIcon, Badge, Group, Loader, Table, Text } from '@mantine/core';
import { useState } from 'react';
import { PiPlay } from 'react-icons/pi';
import { fetchRecordingUrl, useCallsList, type Call } from '@/models/calls';
import { UserAvatar } from '@/components/UserAvatar';
import { CollapsibleCard } from '@/components/CollapsibleCard';

function formatDuration(seconds: number | null): string {
  if (seconds == null) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function RecordingCell({ call }: { call: Call }) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  if (call.recordingStatus === 'pending') return <Loader size="xs" />;
  if (call.recordingStatus !== 'available') {
    return <Text c="dimmed">—</Text>;
  }
  if (url) {
    return <audio controls src={url} style={{ height: 32, maxWidth: 220 }} />;
  }
  return (
    <ActionIcon
      variant="subtle"
      loading={loading}
      onClick={async () => {
        setLoading(true);
        try {
          const { url: recordingUrl } = await fetchRecordingUrl(call.id);
          setUrl(recordingUrl);
        } finally {
          setLoading(false);
        }
      }}
    >
      <PiPlay size={16} />
    </ActionIcon>
  );
}

export function CallsSection({
  leadId,
  contactId,
  withHeader = true,
}: {
  leadId?: number;
  contactId?: number;
  withHeader?: boolean;
}) {
  const { data, isLoading } = useCallsList({ leadId, contactId });
  const calls = data?.calls ?? [];

  return (
    <>
      {withHeader && (
        <Group justify="space-between" mb="xs">
          <Text fw={600}>Calls</Text>
          {calls.length > 0 && (
            <Text size="sm" c="dimmed">
              {calls.length}
            </Text>
          )}
        </Group>
      )}
      {isLoading ? null : calls.length === 0 ? (
        <Text size="sm" c="dimmed">
          No calls yet
        </Text>
      ) : (
        <Table verticalSpacing="xs" fz="sm">
          <Table.Tbody>
            {calls.map((c) => (
              <Table.Tr key={c.id}>
                <Table.Td>{new Date(c.createdAt).toLocaleString()}</Table.Td>
                <Table.Td>
                  <Badge
                    color={c.disposition === 'answered' ? 'green' : 'red'}
                    variant="light"
                  >
                    {c.disposition}
                  </Badge>
                </Table.Td>
                <Table.Td>{c.callerPhone}</Table.Td>
                <Table.Td>
                  {c.answeredByUserId ? (
                    <UserAvatar
                      name={c.answeredByInfo?.name}
                      email={c.answeredByEmail}
                      picture={c.answeredByInfo?.picture}
                      size={24}
                    />
                  ) : (
                    <Text c="dimmed">—</Text>
                  )}
                </Table.Td>
                <Table.Td>{formatDuration(c.durationSeconds)}</Table.Td>
                <Table.Td>
                  <RecordingCell call={c} />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </>
  );
}

export function CallsCard(target: { leadId?: number; contactId?: number }) {
  return (
    <CollapsibleCard title="Calls">
      <CallsSection {...target} withHeader={false} />
    </CollapsibleCard>
  );
}
